import { useState, useEffect, useRef, useContext } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Alert, Button } from '@mui/material'

import { getSessionById, updateSessionStatus, updateSessionFields } from '../../../service/sessionService'
import { SessionDataContext } from '../context/SessionDataContext'
import { showToast } from '../../commonUnit/Toast'
import { studentPathFor } from '../../session/studentView/studentSurface'
import { conditionTagOf } from '../../session/experiment/experimentConfig'
import { card, cardHeader, cardSubtitle, cardTitle, dashboardPage } from './dashboardStyles'

// `notice`: shown above the controls. Left out, a session that is one condition of an
// experiment gets a reminder that these controls affect it alone; null shows nothing.
//
// `controller` (optional): run these same controls for something other than this session's
// own row - the experiment page runs every condition of an experiment with them. The timer
// is still read from SessionDataContext's `session` (there, the experiment's row).
//   start(minutes), resume(), stop(), reset(), setDuration(minutes), expire()
//                    each resolves to whether it worked; they replace the writes below
//   studentCount     shown instead of polling this session's active_stu
//   pausedSeconds    the time left remembered at a pause (status 'closed')
//   linkCard         replaces the Session Link card
//   scope            added to every confirmation, e.g. 'every condition'
export default function SessionLobby({ sessionId, onActiveChange, notice, controller = null }) {
  const TIME_OPTIONS = [5, 10, 15, 20, 25, 30, 45, 60]
  const { session } = useContext(SessionDataContext)
  const [selectedMinutes, setSelectedMinutes] = useState(15)
  const [isActive, setIsActive] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(15 * 60)
  const [studentCount, setStudentCount] = useState(0)
  const [confirmAction, setConfirmAction] = useState(null) // 'start' | 'resume' | 'stop' | 'reset'
  const intervalRef = useRef(null)
  // Absolute wall-clock moment (ms) the current active period is counting down from - see
  // the countdown effect below for why this exists instead of decrementing remainingSeconds
  // directly.
  const startedAtMsRef = useRef(null)

  // Seed/re-sync from the server whenever SessionDataContext's `session` changes - on first
  // load (a page refresh or a second instructor tab), and on every ~10s poll after that (see
  // SessionDataContext.js). Deliberately keeps re-deriving from server truth on every poll,
  // not just once: the incident this fixed was an instructor pressing Start, the UI
  // optimistically showing "Active," but the write itself failing (or landing against a
  // stale backend deploy) so the server never actually recorded it - switching to another
  // Monitor sub-tab and back "looked reset" because it WAS still reset, just not yet visible
  // locally. Continuously re-deriving here means that kind of drift self-corrects within one
  // poll cycle instead of only surfacing on a full page refresh. session.duration_minutes is
  // the opt-in marker for the timed-session flow (see models/session.js) - null means this
  // session has never used the timer, so stay at the untouched defaults above. This is safe
  // to re-run on every poll because there's no free-text local editing happening once
  // hasStarted is true (just Start/Stop/Reset buttons) - unlike TestList.jsx's equivalent
  // fix, there's no in-progress typing here for a poll to clobber.
  useEffect(() => {
    if (!session || session.duration_minutes == null) return
    setSelectedMinutes(session.duration_minutes)
    if (!session.started_at) {
      if (session.status === 'pending') {
        setHasStarted(false)
        setIsActive(false)
      }
      return
    }
    const startedAtMs = new Date(session.started_at).getTime()
    const elapsed = (Date.now() - startedAtMs) / 1000
    const remaining = Math.max(0, Math.round(session.duration_minutes * 60 - elapsed))
    if (session.status === 'active') {
      startedAtMsRef.current = startedAtMs
      setHasStarted(true)
      setIsActive(remaining > 0)
      setRemainingSeconds(remaining)
    } else if (session.status === 'closed') {
      // Paused, or genuinely ended - either way, "Resume" should pick up roughly where
      // things left off. Exact remaining time at the moment Stop was pressed isn't
      // persisted, so this approximates from the original start and can drift across
      // multiple pause/resume cycles - good enough to survive a refresh, not exact. (A
      // controller that remembers it passes pausedSeconds.)
      setHasStarted(true)
      setIsActive(false)
      setRemainingSeconds(controller?.pausedSeconds ?? remaining)
    }
  }, [session, controller?.pausedSeconds]) // eslint-disable-line react-hooks/exhaustive-deps

  // The student page for this session's type (studentSurface.js). Until the session loads,
  // show /workspace/:id: it works for every type, as WorkspacePage sends students of the
  // types it can't run to /session/:id.
  const joinLink = `https://vizpi.org${session ? studentPathFor(session) : `/workspace/${sessionId}`}`

  // Warn before leaving tab while session is active
  useEffect(() => {
    if (!isActive) return

    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isActive])

  // Poll for student count every 5 seconds (a controller supplies its own count)
  useEffect(() => {
    if (!sessionId || controller) return

    const fetchCount = async () => {
      try {
        const sessionData = await getSessionById(sessionId)
        setStudentCount(sessionData?.active_stu || 0)
      } catch (err) {
        console.error('Error fetching student count:', err)
      }
    }

    fetchCount()
    const poll = setInterval(fetchCount, 5000)
    return () => clearInterval(poll)
  }, [sessionId, controller])
  const shownStudentCount = controller ? controller.studentCount || 0 : studentCount

  // Countdown timer - recomputes from startedAtMsRef (absolute wall-clock time) on every
  // tick rather than decrementing remainingSeconds. A plain decrement free-runs on the
  // browser's setInterval, which Chrome throttles for backgrounded tabs - confirmed this
  // drifting badly in testing (instructor tab showed 2+ minutes remaining while the
  // server-computed/student-visible time had already hit zero, because the instructor tab
  // was in the background). Recomputing from an absolute timestamp each tick self-corrects
  // regardless of how late/throttled that tick fires, instead of accumulating error.
  useEffect(() => {
    if (!isActive) {
      clearInterval(intervalRef.current)
      return
    }
    const tick = () => {
      const durationSeconds = selectedMinutes * 60
      const elapsed = (Date.now() - startedAtMsRef.current) / 1000
      const remaining = Math.max(0, Math.round(durationSeconds - elapsed))
      setRemainingSeconds(remaining)
      if (remaining <= 0) {
        clearInterval(intervalRef.current)
        setIsActive(false)
        if (controller) controller.expire()
        else updateSessionStatus(sessionId, 'closed')
      }
    }
    tick()
    intervalRef.current = setInterval(tick, 1000)
    return () => clearInterval(intervalRef.current)
  }, [isActive, selectedMinutes, sessionId]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleStart = async () => {
    // Fresh start: started_at = now, duration_minutes = whatever's selected. Together these
    // are the opt-in marker that puts students in the waiting room until this session is
    // active and lets them render their own countdown from the same two fields.
    const startedAtMs = Date.now()
    const ok = controller
      ? await controller.start(selectedMinutes)
      : await updateSessionFields(sessionId, {
          status: 'active',
          started_at: new Date(startedAtMs).toISOString(),
          duration_minutes: selectedMinutes,
        })
    setConfirmAction(null)
    // Only flip local state if the write actually succeeded - a silent failure here (network
    // blip, or a request that landed against a stale backend deploy) used to leave the UI
    // showing "Active" with a ticking timer while the server still had the session pending,
    // so students never left the waiting room and the instructor's own countdown would
    // "reset" back to Inactive the moment anything re-synced from the server (switching
    // Monitor sub-tabs, a refresh, or the next poll). See SessionDataContext.js's poll.
    if (!ok) {
      // A controller reports its own failures (with the server's reason).
      if (!controller) showToast('Could not start the session - check your connection and try again.', 'error')
      return
    }
    startedAtMsRef.current = startedAtMs
    setIsActive(true)
    setHasStarted(true)
    onActiveChange?.(true)
  }

  const handleResume = async () => {
    // Resuming from a pause, not a fresh start - duration_minutes stays what it was, but
    // started_at is shifted forward by the time already elapsed (duration - remainingSeconds)
    // so the elapsed-time-based countdown (here and on the student side) picks up from where
    // Stop left off instead of restarting the full duration.
    const elapsedBeforePause = selectedMinutes * 60 - remainingSeconds
    const startedAtMs = Date.now() - elapsedBeforePause * 1000
    const ok = controller
      ? await controller.resume()
      : await updateSessionFields(sessionId, {
          status: 'active',
          started_at: new Date(startedAtMs).toISOString(),
        })
    setConfirmAction(null)
    if (!ok) {
      // A controller reports its own failures (with the server's reason).
      if (!controller) showToast('Could not resume the session - check your connection and try again.', 'error')
      return
    }
    startedAtMsRef.current = startedAtMs
    setIsActive(true)
    onActiveChange?.(true)
  }

  const handleStop = async () => {
    const ok = controller ? await controller.stop() : await updateSessionStatus(sessionId, 'closed')
    setConfirmAction(null)
    if (!ok) {
      // A controller reports its own failures (with the server's reason).
      if (!controller) showToast('Could not stop the session - check your connection and try again.', 'error')
      return
    }
    setIsActive(false)
    onActiveChange?.(false)
  }

  const handleReset = async () => {
    // Clears duration_minutes/started_at, not just status - this is the escape hatch for an
    // instructor who pressed Start once and wants out of the timed-session flow entirely.
    // Leaving duration_minutes set here would permanently gate every student behind the
    // waiting room for this session with no UI to undo it.
    const ok = controller
      ? await controller.reset()
      : await updateSessionFields(sessionId, { status: 'pending', started_at: null, duration_minutes: null })
    setConfirmAction(null)
    if (!ok) {
      // A controller reports its own failures (with the server's reason).
      if (!controller) showToast('Could not reset the session - check your connection and try again.', 'error')
      return
    }
    setIsActive(false)
    setHasStarted(false)
    setRemainingSeconds(selectedMinutes * 60)
    onActiveChange?.(false)
  }

  const handleTimeChange = (e) => {
    const mins = Number(e.target.value)
    setSelectedMinutes(mins)
    if (!isActive) {
      setRemainingSeconds(mins * 60)
    }
    // Persist duration_minutes as soon as the instructor picks a duration, not only once
    // they press Start. Without this, duration_minutes and status both flip at the same
    // instant (in handleStart) and a student who loads the page in between - after the
    // instructor opens this tab but before they press Start - never sees the waiting room,
    // since status is still 'pending' with duration_minutes still null: the exact "Waiting
    // for Instructor to Start Session" window this feature exists for. This only fires on an
    // actual dropdown selection (this dropdown is only shown pre-Start - see the isEditable
    // check below), not merely from opening the tab, so it stays a deliberate opt-in rather
    // than something every session picks up just by an instructor glancing at Monitor/Lobby.
    if (controller) controller.setDuration(mins)
    else updateSessionFields(sessionId, { duration_minutes: mins })
  }

  // Confirmation dialog config
  const confirmDialogs = {
    start: {
      title: 'Start Session?',
      message: `This will release ${shownStudentCount} student${shownStudentCount !== 1 ? 's' : ''} from the waiting room into the task. The ${selectedMinutes}-minute timer will begin. Make sure all students have joined before starting.`,
      confirmText: 'Start Session',
      confirmColor: '#059669',
      onConfirm: handleStart,
    },
    resume: {
      title: 'Resume Session?',
      message: 'Students will be returned to the coding task and the timer will continue.',
      confirmText: 'Resume Session',
      confirmColor: '#059669',
      onConfirm: handleResume,
    },
    stop: {
      title: 'Pause Session?',
      message: 'Students will see the post survey and won\'t be able to code until you resume. If a student starts filling out the survey, they may think the session is over.',
      confirmText: 'Pause Session',
      confirmColor: '#dc2626',
      onConfirm: handleStop,
    },
    reset: {
      title: 'Reset Session?',
      message: 'All students will be placed back in the waiting room and the timer will reset.',
      confirmText: 'Reset',
      confirmColor: '#dc2626',
      onConfirm: handleReset,
    },
  }
  if (controller?.scope) {
    Object.values(confirmDialogs).forEach((dialog) => {
      dialog.message = `${dialog.message} This applies to ${controller.scope}.`
    })
  }

  return (
    <div style={dashboardPage}>

      {notice === undefined && conditionTagOf(session) ? (
        <Alert
          severity="warning"
          action={
            <Button
              component={RouterLink}
              to={`/session/${conditionTagOf(session).experimentId}`}
              color="inherit"
              size="small">
              Open experiment
            </Button>
          }>
          This session is one condition of an experiment. These controls start, pause and reset this
          condition only. To run every condition together, use the experiment&apos;s page.
        </Alert>
      ) : (
        notice || null
      )}

      {/* ── Session Link Card ── */}
      {controller?.linkCard ? controller.linkCard : (
      <div style={{ ...card, flexShrink: 0 }}>
        <div style={cardHeader}>
          <h3 style={cardTitle}>Session Link</h3>
        </div>
        <p style={cardSubtitle}>Share this link with your students to join</p>

        <div style={{
          padding: '20px 24px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          fontFamily: 'Monaco, Menlo, monospace',
          fontSize: '48px',
          fontWeight: 700,
          color: '#2563eb',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {joinLink}
        </div>
      </div>
      )}

      {/* ── Bottom Row: Students + Timer ── */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        minHeight: 0,
      }}>

        {/* ── Students Card ── */}
        <div style={{
          ...card,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={cardHeader}>
            <h3 style={cardTitle}>Students</h3>
          </div>
          <p style={cardSubtitle}>Currently in the session</p>

          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}>
            <div style={{
              fontSize: '120px',
              fontWeight: 800,
              color: '#111',
              lineHeight: 1,
            }}>
              {shownStudentCount}
            </div>
            <div style={{
              fontSize: '18px',
              color: '#9ca3af',
              marginTop: '4px',
            }}>
              {shownStudentCount === 0 ? 'Waiting for students to join...' : 'Students connected'}
            </div>
          </div>
        </div>

        {/* ── Session Timer Card ── */}
        <div style={{
          ...card,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={cardHeader}>
                <h3 style={cardTitle}>Session Timer</h3>
              </div>
              <p style={cardSubtitle}>Control the session duration</p>
            </div>

            {/* Status badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              borderRadius: '20px',
              backgroundColor: isActive ? '#ecfdf5' : hasStarted ? '#fefce8' : '#f3f4f6',
              border: `1px solid ${isActive ? '#a7f3d0' : hasStarted ? '#fde68a' : '#e5e7eb'}`,
              marginTop: '2px',
            }}>
              <div style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                backgroundColor: isActive ? '#10b981' : hasStarted ? '#f59e0b' : '#9ca3af',
              }} />
              <span style={{
                fontSize: '12px',
                fontWeight: 500,
                color: isActive ? '#059669' : hasStarted ? '#d97706' : '#6b7280',
              }}>
                {isActive ? 'Active' : hasStarted ? 'Paused' : 'Inactive'}
              </span>
            </div>
          </div>

          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '28px',
          }}>
            {/* 5-minute warning */}
            {isActive && remainingSeconds <= 300 && remainingSeconds > 0 && (
              <div style={{
                padding: '8px 16px',
                backgroundColor: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#92400e',
                textAlign: 'center',
              }}>
                ⚠️ Less than {Math.ceil(remainingSeconds / 60)} minute{Math.ceil(remainingSeconds / 60) !== 1 ? 's' : ''} remaining
              </div>
            )}

            {!hasStarted ? (
              <select
                value={selectedMinutes}
                onChange={handleTimeChange}
                style={{
                  fontSize: '80px',
                  fontWeight: 700,
                  fontFamily: 'Monaco, Menlo, monospace',
                  color: '#d1d5db',
                  letterSpacing: '4px',
                  lineHeight: 1,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  outline: 'none',
                  textAlign: 'center',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                }}
              >
                {TIME_OPTIONS.map(min => (
                  <option key={min} value={min}>
                    {min.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            ) : (
              <div style={{
                fontSize: '80px',
                fontWeight: 700,
                fontFamily: 'Monaco, Menlo, monospace',
                color: remainingSeconds <= 60 ? '#dc2626' : isActive ? '#111' : '#6b7280',
                letterSpacing: '4px',
                lineHeight: 1,
              }}>
                {formatTime(remainingSeconds)}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              {isActive ? (
                <button
                  onClick={() => setConfirmAction('stop')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '14px 32px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="none">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                  </svg>
                  Stop Session
                </button>
              ) : (
                <button
                  onClick={() => setConfirmAction(hasStarted ? 'resume' : 'start')}
                  disabled={remainingSeconds === 0}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '14px 32px',
                    backgroundColor: remainingSeconds === 0 ? '#d1d5db' : '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: remainingSeconds === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="none">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  {hasStarted ? 'Resume' : 'Start Session'}
                </button>
              )}
              <button
                onClick={() => setConfirmAction('reset')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
                Reset
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* ── Confirmation Dialog ── */}
      {confirmAction && confirmDialogs[confirmAction] && (
        <div
          onClick={() => setConfirmAction(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '420px',
              width: '90%',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{
              fontSize: '16px',
              fontWeight: 700,
              color: '#111',
              marginBottom: '8px',
            }}>
              {confirmDialogs[confirmAction].title}
            </div>
            <div style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: 1.6,
              marginBottom: '24px',
            }}>
              {confirmDialogs[confirmAction].message}
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d0d5dd',
                  background: 'white',
                  color: '#333',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDialogs[confirmAction].onConfirm}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmDialogs[confirmAction].confirmColor,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {confirmDialogs[confirmAction].confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}